import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import $ from "jquery";
import "datatables.net-dt";
import "datatables.net-buttons/js/dataTables.buttons";
import "datatables.net-buttons/js/buttons.html5";
import "datatables.net-dt/css/jquery.dataTables.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import Swal from "sweetalert2";
import { Helmet } from "react-helmet";
import Select from "react-select";
import * as XLSX from "xlsx";
import withTokenCheck from "../../withTokenCheck";

const emptyForm = { Direccion: "", CodigoRelacionado: "", origen_id: "" };

const normalizeSearchText = (value) =>
    (value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

const DireccionOrigenHomologacion = ({ token }) => {
    const [rows, setRows] = useState([]);
    const [origenes, setOrigenes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [file, setFile] = useState(null);
    const tableRef = useRef(null);
    const municipioOptions = useMemo(
        () =>
            origenes.map((origen) => ({
                value: origen.id,
                label: `${origen.codigo} - ${origen.nombre}`,
                codigo: origen.codigo,
                searchText: normalizeSearchText(`${origen.codigo} ${origen.nombre}`),
            })),
        [origenes]
    );
    const selectedMunicipio =
        municipioOptions.find((option) => String(option.value) === String(form.origen_id)) || null;
    const filterMunicipioOption = (option, inputValue) =>
        option.data.searchText.includes(normalizeSearchText(inputValue));

    const fetchRows = useCallback(() => {
        if (!token) return;
        const headers = { Authorization: `Bearer ${token}` };
        Promise.all([
            axios.get("/direccionOrigenHomologacion", { headers }),
            axios.get("/origen", { headers }),
        ])
            .then(([direcciones, origenesResponse]) => {
                setRows(direcciones.data);
                setOrigenes(origenesResponse.data);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [token]);

    useEffect(fetchRows, [fetchRows]);

    useEffect(() => {
        if (!loading && tableRef.current) {
            const table = $(tableRef.current).DataTable({
                destroy: true,
                paging: true,
                ordering: true,
                info: true,
                language: { url: "//cdn.datatables.net/plug-ins/1.13.1/i18n/es-ES.json" },
                dom: "<'row'<'col-sm-2'l><'col-sm-6'B><'col-sm-4'f>><'row'<'col-sm-12'tr>><'row'<'col-sm-6 text-right'i><'col-sm-6'p>>",
                buttons: [
                    {
                        extend: "excel",
                        className: "btn btn-success btn-sm px-3",
                        text: "Descargar Excel",
                        titleAttr: "Exportar homologaciones a Excel",
                        extension: ".xlsx",
                    },
                ],
            });
            $(table.buttons().container())
                .addClass("d-flex align-items-center")
                .find("button")
                .removeClass("dt-button")
                .addClass("btn btn-success btn-sm px-3 fw-semibold");
            return () => table.destroy();
        }
    }, [loading, rows]);

    const openCreate = () => {
        setEditing(null);
        setForm(emptyForm);
        setModalOpen(true);
    };

    const openEdit = (row) => {
        setEditing(row);
        setForm({
            Direccion: row.Direccion || "",
            CodigoRelacionado: row.origen?.codigo || row.CodigoRelacionado || "",
            origen_id: row.origen_id || "",
        });
        setModalOpen(true);
    };

    const setOrigen = (option) => {
        setForm({
            ...form,
            origen_id: option?.value || "",
            CodigoRelacionado: option?.codigo || "",
        });
    };

    const save = (event) => {
        event.preventDefault();
        const request = editing
            ? axios.put(`/direccionOrigenHomologacion/${editing.id}`, form, { headers: { Authorization: `Bearer ${token}` } })
            : axios.post("/direccionOrigenHomologacion", form, { headers: { Authorization: `Bearer ${token}` } });

        request
            .then(() => {
                Swal.fire("Guardado", "La homologacion fue guardada.", "success").then(() => {
                    setModalOpen(false);
                    fetchRows();
                });
            })
            .catch((error) => {
                const message = error.response?.data?.message || "No fue posible guardar la homologacion.";
                Swal.fire("Error", message, "error");
            });
    };

    const remove = (row) => {
        axios
            .delete(`/direccionOrigenHomologacion/${row.id}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(() => {
                Swal.fire("Eliminado", "La homologacion fue eliminada.", "success").then(fetchRows);
            })
            .catch(() => Swal.fire("Error", "No fue posible eliminar la homologacion.", "error"));
    };

    const downloadTemplate = () => {
        const worksheet = XLSX.utils.json_to_sheet([{ Direccion: "", CodigoRelacionado: "" }], {
            header: ["Direccion", "CodigoRelacionado"],
        });
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Homologacion");
        XLSX.writeFile(workbook, "plantilla_homologacion_direcciones.xlsx");
    };

    const uploadFile = () => {
        if (!file) {
            Swal.fire("Archivo requerido", "Seleccione un archivo para cargar.", "warning");
            return;
        }

        const formData = new FormData();
        formData.append("archivo", file);

        axios
            .post("/direccionOrigenHomologacion/import", formData, {
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
            })
            .then((response) => {
                const data = response.data;
                Swal.fire(
                    "Carga finalizada",
                    `Insertados: ${data.insertados}. Omitidos: ${data.omitidos}.`,
                    data.errores?.length ? "warning" : "success"
                ).then(fetchRows);
            })
            .catch(() => Swal.fire("Error", "No fue posible importar el archivo.", "error"));
    };

    return (
        <>
            <Helmet>
                <title>Homologacion Direcciones</title>
            </Helmet>
            <div style={{ maxWidth: "1200px", margin: "50px auto" }}>
                <div className="d-flex gap-2 mb-3 flex-wrap">
                    <Button color="primary" onClick={openCreate}>Crear</Button>
                    <Button color="success" onClick={downloadTemplate}>Descargar plantilla</Button>
                    <input className="form-control" style={{ maxWidth: 360 }} type="file" accept=".xlsx,.xls,.csv" onChange={(event) => setFile(event.target.files[0])} />
                    <Button color="secondary" onClick={uploadFile}>Cargar archivo</Button>
                </div>
                {loading ? (
                    <div>Cargando...</div>
                ) : (
                    <div style={{ width: "100%", overflowX: "auto" }}>
                        <table ref={tableRef} className="table" style={{ width: "100%" }}>
                            <thead>
                                <tr>
                                    <th>Editar</th>
                                    <th>Eliminar</th>
                                    <th>Direccion</th>
                                    <th>Codigo Relacionado</th>
                                    <th>Usuario creacion</th>
                                    <th>Fecha creacion</th>
                                    <th>Usuario actualizacion</th>
                                    <th>Fecha actualizacion</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row.id}>
                                        <td><Button color="secondary" onClick={() => openEdit(row)}>Editar</Button></td>
                                        <td><Button color="danger" onClick={() => remove(row)}>Eliminar</Button></td>
                                        <td>{row.Direccion}</td>
                                        <td>{row.origen?.codigo || row.CodigoRelacionado || ""}</td>
                                        <td>{row.created_by?.name || "no existe informacion"}</td>
                                        <td>{row.created_at || "Informacion no disponible"}</td>
                                        <td>{row.updated_by?.name || "no existe informacion"}</td>
                                        <td>{row.updated_at || "Informacion no disponible"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)}>
                <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
                    {editing ? "Editar homologacion" : "Crear homologacion"}
                </ModalHeader>
                <ModalBody>
                    <label>Direccion</label>
                    <textarea className="form-control mb-3" value={form.Direccion} onChange={(event) => setForm({ ...form, Direccion: event.target.value })} />
                    <label>Municipio origen</label>
                    <Select
                        className="mb-3"
                        isClearable
                        isSearchable
                        placeholder="Buscar municipio por codigo o nombre"
                        noOptionsMessage={() => "Sin coincidencias"}
                        filterOption={filterMunicipioOption}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        options={municipioOptions}
                        value={selectedMunicipio}
                        onChange={setOrigen}
                    />
                    <label>Codigo relacionado</label>
                    <input className="form-control" value={form.CodigoRelacionado} readOnly />
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={save}>Guardar</Button>
                    <Button color="danger" onClick={() => setModalOpen(false)}>Cancelar</Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default withTokenCheck(DireccionOrigenHomologacion);
